import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Dialog,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import EventIcon from '@mui/icons-material/Event';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import Masonry from '@mui/lab/Masonry';

const IMAGES_PER_PAGE = 12;

function Gallery() {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allImages, setAllImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (events.length > 0) {
      fetchAllImages();
    }
  }, [events]);

  useEffect(() => {
    filterAndDisplayImages();
  }, [selectedEvent, allImages, currentPage]);

  const fetchEvents = async () => {
    try {
      const eventsRef = collection(db, 'gallery_events');
      const querySnapshot = await getDocs(eventsRef);
      const eventsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
      setEvents(eventsList);
      setLoading(false);
    } catch (error) {
      setError('Error fetching events: ' + error.message);
      setLoading(false);
    }
  };

  const fetchAllImages = async () => {
    setLoading(true);
    try {
      const allImagesArray = [];
      for (const event of events) {
        const eventDoc = await getDoc(doc(db, 'gallery_events', event.id));
        if (eventDoc.exists()) {
          const eventData = eventDoc.data();
          if (eventData.images) {
            const imagesWithEvent = eventData.images.map(img => ({
              ...img,
              eventName: event.name,
              eventId: event.id
            }));
            allImagesArray.push(...imagesWithEvent);
          }
        }
      }
      setAllImages(allImagesArray);
    } catch (error) {
      setError('Error fetching images: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterAndDisplayImages = () => {
    let filteredImages = [];
    if (selectedEvent === 'all') {
      filteredImages = [...allImages].sort(() => Math.random() - 0.5);
    } else {
      filteredImages = allImages.filter(img => img.eventId === selectedEvent);
    }
    
    const startIndex = currentPage * IMAGES_PER_PAGE;
    setDisplayedImages(filteredImages.slice(startIndex, startIndex + IMAGES_PER_PAGE));
  };

  const handleEventSelect = (eventId) => {
    setSelectedEvent(eventId);
    setCurrentPage(0);
    setSelectedImage(null);
  };

  const handleNextPage = () => {
    const filteredImages = selectedEvent === 'all' 
      ? allImages 
      : allImages.filter(img => img.eventId === selectedEvent);
    
    const maxPages = Math.ceil(filteredImages.length / IMAGES_PER_PAGE);
    if (currentPage < maxPages - 1) {
      setCurrentPage(prev => prev + 1);
    } else {
      setCurrentPage(0); // Loop back to first page
    }
  };

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const handleCloseDialog = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      setSelectedImage(displayedImages[currentImageIndex - 1]);
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < displayedImages.length - 1) {
      setSelectedImage(displayedImages[currentImageIndex + 1]);
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        width: '100%',
        bgcolor: '#f8f9fa',
        pt: { xs: 3, sm: 4 },
        pb: { xs: 3, sm: 4 }
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: '#580100',
              mb: 0.5
            }}
          >
            Photo Gallery
          </Typography>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 1,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem'
                }
              }}
            >
              {error}
            </Alert>
          )}
        </Box>

        <Grid container spacing={2}>
          {/* Events List */}
          <Grid item xs={12} md={3}>
            <Paper 
              elevation={0}
              sx={{ 
                borderRadius: 1,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
                height: '100%'
              }}
            >
              <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Events
                </Typography>
              </Box>
              <Divider />
              <List sx={{ p: 0.5 }}>
                <ListItem
                  button
                  dense
                  selected={selectedEvent === 'all'}
                  onClick={() => handleEventSelect('all')}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    bgcolor: selectedEvent === 'all' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': {
                      bgcolor: selectedEvent === 'all' 
                        ? alpha(theme.palette.primary.main, 0.15) 
                        : alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <PhotoLibraryIcon 
                      color={selectedEvent === 'all' ? 'primary' : 'inherit'} 
                      sx={{ fontSize: '1.2rem' }}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography 
                        variant="body2" 
                        color={selectedEvent === 'all' ? 'primary' : 'textPrimary'}
                        sx={{ fontWeight: selectedEvent === 'all' ? 600 : 400 }}
                      >
                        All Events ({allImages.length})
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider sx={{ my: 0.5 }} />
                {events.map((event) => (
                  <ListItem
                    key={event.id}
                    button
                    dense
                    selected={selectedEvent === event.id}
                    onClick={() => handleEventSelect(event.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: selectedEvent === event.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      '&:hover': {
                        bgcolor: selectedEvent === event.id 
                          ? alpha(theme.palette.primary.main, 0.15) 
                          : alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <EventIcon 
                        color={selectedEvent === event.id ? 'primary' : 'inherit'} 
                        sx={{ fontSize: '1.2rem' }}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography 
                          variant="body2" 
                          color={selectedEvent === event.id ? 'primary' : 'textPrimary'}
                          sx={{ fontWeight: selectedEvent === event.id ? 600 : 400 }}
                        >
                          {event.name} ({event.images?.length || 0})
                        </Typography>
                      }
                      secondary={
                        event.date && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {new Date(event.date).toLocaleDateString()}
                          </Typography>
                        )
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Images Section */}
          <Grid item xs={12} md={9}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                minHeight: 300
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : displayedImages.length > 0 ? (
                <>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {selectedEvent === 'all' ? 'All Events' : events.find(e => e.id === selectedEvent)?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Masonry columns={{ xs: 2, sm: 3, md: 4, lg: 5 }} spacing={1.5}>
                    {displayedImages.map((image, index) => (
                      <Card
                        key={index}
                        elevation={0}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: theme.shadows[2]
                          }
                        }}
                        onClick={() => handleImageClick(image, index)}
                      >
                        <CardMedia
                          component="img"
                          image={image.url}
                          alt={`Gallery image ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: 'auto',
                            aspectRatio: '4/3',
                            objectFit: 'cover'
                          }}
                        />
                      </Card>
                    ))}
                  </Masonry>
                  {/* Only show Load More if there are more images to display */}
                  {((selectedEvent === 'all' && displayedImages.length < allImages.length) ||
                    (selectedEvent !== 'all' && events.find(e => e.id === selectedEvent)?.images?.length > displayedImages.length)) && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleNextPage}
                        endIcon={<NavigateNextIcon sx={{ fontSize: '1.2rem' }} />}
                        sx={{
                          px: 3,
                          py: 0.5,
                          borderRadius: 1,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.875rem'
                        }}
                      >
                        Load More
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300,
                    color: 'text.secondary'
                  }}
                >
                  <PhotoLibraryIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
                  <Typography variant="body2">No Images Available</Typography>
                  <Typography variant="caption">Select an event to view its images</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Image Preview Dialog */}
        <Dialog
          open={Boolean(selectedImage)}
          onClose={handleCloseDialog}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'black',
              boxShadow: 'none',
              position: 'relative',
              m: { xs: 0 },
              width: '100%',
              height: '100%'
            }
          }}
        >
          {selectedImage && (
            <Box 
              sx={{ 
                position: 'relative',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <IconButton
                onClick={handleCloseDialog}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.7)'
                  },
                  zIndex: 1
                }}
              >
                <CloseIcon />
              </IconButton>

              <IconButton
                onClick={handlePreviousImage}
                disabled={currentImageIndex === 0}
                sx={{
                  position: 'absolute',
                  left: { xs: 4, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.7)'
                  },
                  zIndex: 1,
                  p: { xs: 1, sm: 2 }
                }}
              >
                <NavigateBeforeIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
              </IconButton>

              <IconButton
                onClick={handleNextImage}
                disabled={currentImageIndex === displayedImages.length - 1}
                sx={{
                  position: 'absolute',
                  right: { xs: 4, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.7)'
                  },
                  zIndex: 1,
                  p: { xs: 1, sm: 2 }
                }}
              >
                <NavigateNextIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
              </IconButton>

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden'
                }}
              >
                <Box
                  component="img"
                  src={selectedImage.url}
                  alt="Selected gallery image"
                  sx={{
                    maxHeight: '100%',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>

              <Box
                sx={{
                  width: '100%',
                  bgcolor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  p: { xs: 1.5, sm: 2 },
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {selectedImage.eventName}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {new Date(selectedImage.uploadedAt.seconds * 1000).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          )}
        </Dialog>
      </Container>
    </Box>
  );
}

export default Gallery;
